import React, {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react'

import { changeLanguage } from 'src/components/LanguagePicker'

import { Organization, User } from '../types'
import * as MeService from '../services/api/me'
import ApiService from '../services/api'
import AuthService, { AuthByUsernameProps } from '../services/api/auth'
import OrganizationsService from '../services/api/organizations'

type AuthContextValues = {
  accessToken: string
  authByQRCode(props: AuthByQRCodeProps): Promise<{ error?: string }>
  authByUsername(props: AuthByUsernameProps): Promise<{ error?: string }>
  logout(): void
  organization: Organization
  user: User
}

type AuthByQRCodeProps = {
  userKey: string
}

type Props = PropsWithChildren<{
  clearStorage(): void
  getItemStorage(key: string): string | null
  loader: ReactNode
  setItemStorage(key: string, value: string): void
}>

const AuthContext = createContext<AuthContextValues>({} as AuthContextValues)

export function useAuthContext(): AuthContextValues {
  return useContext(AuthContext)
}

export default function AuthProvider({
  children,
  clearStorage,
  getItemStorage,
  loader,
  setItemStorage
}: Props): JSX.Element {
  const [accessToken, setAccessToken] = useState<string>()
  const [organization, setOrganization] = useState<Organization>()
  const [ready, setReady] = useState(false)
  const [user, setUser] = useState<User>()

  const authSuccess: (
    token: AuthContextValues['accessToken']
  ) => ReturnType<AuthContextValues['authByQRCode']> = useCallback(
    async (token: AuthContextValues['accessToken']) => {
      setAccessToken(token)
      setItemStorage('accessToken', token)

      // necessary for setAccessToken to complete
      await new Promise(resolve => setTimeout(resolve, 500))

      const meResponse = await MeService.getMe()

      if (meResponse.error) {
        return { error: meResponse.error }
      }

      setUser(meResponse.data)
      setItemStorage('user', JSON.stringify(meResponse.data))
      if (meResponse.data.must_change_password) {
        changeLanguage(meResponse.data.preferred_language.toLowerCase())
      }

      const organizationResponse = await OrganizationsService.getById(
        meResponse.data.organization_id
      )

      if (organizationResponse.error) {
        return { error: meResponse.error }
      }

      setOrganization(organizationResponse.data)
      setItemStorage('organization', JSON.stringify(organizationResponse.data))

      return {}
    },
    [setItemStorage]
  )

  const authByQRCode: AuthContextValues['authByQRCode'] = useCallback(
    async ({ userKey }: AuthByQRCodeProps) => {
      return authSuccess('---token-test---')
    },
    [authSuccess]
  )

  const authByUsername: AuthContextValues['authByUsername'] = useCallback(
    async ({ login, password }: AuthByUsernameProps) => {
      const response = await AuthService.authByUsername({ login, password })

      if (response.ok === true) {
        return authSuccess(response.data.access_token)
      }

      const { problem, status, data } = response
      if (problem === 'TIMEOUT_ERROR') {
        return { error: 'common.response.timeout-error' }
      }
      if (!status || problem === 'NETWORK_ERROR') {
        return { error: 'common.response.network-error' }
      }
      if (
        status === 401 &&
        data.reasons === 'AUTHENTICATION_INVALID_CREDENTIALS'
      ) {
        return { error: 'web.login.invalid-credentials' }
      }
      if (status === 500) {
        return { error: 'common.response.status-500' }
      }
      return { error: 'common.response.unexpected-error' }
    },
    [authSuccess]
  )

  const logout = useCallback(() => {
    setAccessToken(null)
    setOrganization(null)
    setUser(null)

    clearStorage()
  }, [clearStorage])

  useEffect(() => {
    try {
      const [storedAccessToken, storedOrganization, storedUser] = [
        getItemStorage('accessToken'),
        getItemStorage('organization'),
        getItemStorage('user')
      ]

      setAccessToken(storedAccessToken)
      setOrganization(JSON.parse(storedOrganization))
      setUser(JSON.parse(storedUser))
    } catch (e) {
      //
    } finally {
      setReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const authorization = !accessToken ? '' : `Bearer ${accessToken}`
    ApiService.setHeader('Authorization', authorization)
  }, [accessToken])

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        authByQRCode,
        authByUsername,
        logout,
        organization,
        user
      }}
    >
      {ready ? children : loader}
    </AuthContext.Provider>
  )
}
